/**
 * Typed settings store. User preferences persist across sessions.
 *
 * Schema versions when adding fields; readFrom() migrates legacy payloads
 * by merging into defaults so missing keys don't break the store.
 */
export interface KeyBindings {
  moveUp: string[];
  moveDown: string[];
  moveLeft: string[];
  moveRight: string[];
  consume: string[];
  pause: string[];
  menu: string[];
}

export interface BiomassSettings {
  version: number;
  volume: {
    master: number;
    music: number;
    sfx: number;
    vo: number;
  };
  display: {
    fullscreen: boolean;
    vsync: boolean;
    reducedMotion: boolean;
  };
  accessibility: {
    colorblindMode: 'off' | 'deuteranopia' | 'protanopia' | 'tritanopia';
    textScale: number;
    subtitles: boolean;
    highContrast: boolean;
  };
  controls: {
    bindings: KeyBindings;
    gamepadDeadzone: number;
  };
  gameplay: {
    hardcoreUnlocked: boolean;
    preferredDifficulty: 'normal' | 'hardcore';
  };
}

export const CURRENT_SETTINGS_VERSION = 1;

export const DEFAULT_BINDINGS: KeyBindings = {
  moveUp: ['KeyW', 'ArrowUp'],
  moveDown: ['KeyS', 'ArrowDown'],
  moveLeft: ['KeyA', 'ArrowLeft'],
  moveRight: ['KeyD', 'ArrowRight'],
  consume: ['Space', 'KeyE'],
  pause: ['Escape'],
  menu: ['Tab'],
};

export const DEFAULT_SETTINGS: BiomassSettings = {
  version: CURRENT_SETTINGS_VERSION,
  volume: { master: 0.8, music: 0.7, sfx: 1.0, vo: 1.0 },
  display: { fullscreen: false, vsync: true, reducedMotion: false },
  accessibility: {
    colorblindMode: 'off',
    textScale: 1.0,
    subtitles: true,
    highContrast: false,
  },
  controls: {
    bindings: { ...DEFAULT_BINDINGS },
    gamepadDeadzone: 0.2,
  },
  gameplay: { hardcoreUnlocked: false, preferredDifficulty: 'normal' },
};

export interface SettingsBackend {
  read(): Promise<string | null>;
  write(data: string): Promise<void>;
}

export class InMemorySettingsBackend implements SettingsBackend {
  private data: string | null = null;
  async read(): Promise<string | null> {
    return this.data;
  }
  async write(data: string): Promise<void> {
    this.data = data;
  }
}

export class LocalStorageSettingsBackend implements SettingsBackend {
  constructor(private readonly key = 'biomass:settings') {}
  async read(): Promise<string | null> {
    return localStorage.getItem(this.key);
  }
  async write(data: string): Promise<void> {
    localStorage.setItem(this.key, data);
  }
}

export type SettingsListener = (settings: Readonly<BiomassSettings>) => void;

function deepClone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function deepMerge<T>(defaults: T, incoming: Partial<T> | null | undefined): T {
  if (!incoming || typeof incoming !== 'object') return defaults;
  const out: Record<string, unknown> = { ...(defaults as Record<string, unknown>) };
  for (const [k, v] of Object.entries(incoming as Record<string, unknown>)) {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      out[k] = deepMerge(
        (defaults as Record<string, unknown>)[k] ?? {},
        v as Record<string, unknown>,
      );
    } else if (v !== undefined) {
      out[k] = v;
    }
  }
  return out as T;
}

export class SettingsStore {
  private settings: BiomassSettings;
  private listeners: SettingsListener[] = [];

  constructor(private readonly backend: SettingsBackend = new InMemorySettingsBackend()) {
    this.settings = deepClone(DEFAULT_SETTINGS);
  }

  get snapshot(): Readonly<BiomassSettings> {
    return this.settings;
  }

  async load(): Promise<void> {
    const raw = await this.backend.read();
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw) as Partial<BiomassSettings>;
      this.settings = deepMerge(deepClone(DEFAULT_SETTINGS), parsed);
      this.settings.version = CURRENT_SETTINGS_VERSION;
      this.emit();
    } catch {
      // Corrupted; keep defaults silently
    }
  }

  async save(): Promise<void> {
    await this.backend.write(JSON.stringify(this.settings));
  }

  update(patch: Partial<BiomassSettings>): void {
    this.settings = deepMerge(this.settings, patch);
    this.emit();
  }

  reset(): void {
    this.settings = deepClone(DEFAULT_SETTINGS);
    this.emit();
  }

  onChange(listener: SettingsListener): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  private emit(): void {
    for (const l of this.listeners) l(this.settings);
  }
}
